import React, { useState, useEffect, useMemo, useRef } from 'react'
import globalConfig from 'Helper/configHelpers'
import { useDispatch, useSelector } from 'react-redux'
import { closeAllModals } from 'actions/modalActions'
import { useSearchParams, useParams, useLocation, useNavigate } from 'react-router-dom'
import AuthError from './PanelComponents/AuthError.jsx'
import AuthProviderWrapper from './PanelComponents/AuthProviderWrapper.jsx'
import StatusCheck from './PanelComponents/StatusCheck.jsx'
import StorageCheck from './PanelComponents/StorageCheck.jsx'
import AuthView from './AuthView.jsx'
import { successfulLoginEvent } from 'actions/authActions.js'
import movement from 'GeneralUI/Timer/movement'
import withQueryParams from 'HigherOrder/withQueryParams.jsx'
import userHasPrivs from 'Helper/Auth/userHasPrivs'
import { xtractAPI } from 'actions/apiActions'

const AuthPanel = props => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const params = useParams()
  const auth = useSelector(state => state.auth)
  const [error, setError] = useState(null)
  const [launchContext, setLaunchContext] = useState(null)
  const setErrorRef = useRef()
  const [storageCheckComplete, setStorageCheckComplete] = useState(false)
  const [statusCheckComplete, setStatusCheckComplete] = useState(false)

  const { subProvider, authProvider } = useMemo(() => {
    let authProvider
    let subProvider

    if (params.subProvider && params.subProvider.toLowerCase() === 'xtract') {
      // xtract is the only valid subprovider currently
      subProvider = params.subProvider
      // if xtract is the sub provider, we will try whatever auth provider they gave us, if they get
      // a 404 its their own fault for typing something wrong.
      authProvider = params.authProvider
    } else {
      // here there is no subprovider... meaning this is customer facing auth so we will make an effort
      // to ensure that if they manually typed it, they chose a valid option and if not we will push them
      // to the default.
      if (params.authProvider && globalConfig.auth_providers.find(prov => prov.toLowerCase() === params.authProvider.toLowerCase())) {
        authProvider = params.authProvider
      } else if (globalConfig.auth_providers.length === 1) {
        // if there is only one option and its summit auto select it. Otherwise dont select because even if there is only one option,
        // they still need to see the AuthSelector to click the "login via whatever" button.
        authProvider = globalConfig.auth_providers[0]
      }
    }

    if (typeof subProvider === 'string') {
      subProvider = subProvider.toLowerCase()
    }

    if (typeof authProvider === 'string') {
      authProvider = authProvider.toLowerCase()
    }

    return { subProvider, authProvider }
  }, [params])

  const queryParams = useMemo(() => {
    return Object.fromEntries([...searchParams])
  }, [searchParams])

  useEffect(() => {
    // watch every render for all of the auth details to have been populated meaning the user is fully logged in
    // and able to make the api calls necessary to use the app.
    if (auth && auth.token && auth.user_id && auth.privilege) {
      // the user is logged in at this point so we just need to redirect them.
      movement.start()

      if (subProvider === 'xtract' && userHasPrivs(['sys_admin'])) {
        // We at least need to make sure that privileges (normal in global app config) have been loaded or we wont be
        // able to get to cloud admin.
        const callbacks = {
          200: () => navigate('/xtract/cloud_admin')
        }
        dispatch(
          xtractAPI('privilege','read_all',null, callbacks)
        )
      } else {
        dispatch(successfulLoginEvent())
        // see if there is a saved location in localStorage for this user (or a location with no user
        // indicating this user tried to acccess an auth required path before logging in)
        let storedRedirect = localStorage.getItem('nextLoginPath')
        if(!storedRedirect) {
          // we might not support local storage so try session storage as a fallback
          storedRedirect = sessionStorage.getItem('nextLoginPath')
        }
        const storedLocationDetails = JSON.parse(storedRedirect || '{}')
        // if we received a request to launch in a specific context (auth provider launch in context) then we should use that,
        // otherwise the default is the dashboard.
        let loginLocation = launchContext ? launchContext : '/dashboard'
        if (
          !launchContext &&
          storedLocationDetails.path &&
          (!storedLocationDetails.user_id || storedLocationDetails.user_id === auth.user_id)
        ) {
          // if we aren't doing a launch in context check the previous path from when
          // the system was last used and if that belonged to the same user that is logging back in,
          // send them to the same place. 
          loginLocation = storedLocationDetails.path
        }
        navigate(loginLocation)
      }
    }
  }, [auth.token, auth.user_id, auth.privilege])

  const bypass = queryParams.bypass

  useEffect(() => {
    // if we are bypassing checks or authCallInProgress (meaning we are at the /callback route so we already
    // did the checks but have reloaded after a return from the authProvider) set the flags accordingly
    if (bypass || auth.authCallInProgress) {
      setStatusCheckComplete(true)
      setStorageCheckComplete(true)
    }
  }, [bypass, auth.authCallInProgress])

  const emailVerificationRequired = queryParams.unverified_email === 'true'
  const emailAddressRequired = queryParams.missing_email === 'true'

  /**
   * Above we used query params and search params to determine the following values that will be used below.
   * requiresEmailVerification - the user has an email but its unverified
   * emailAddressRequired - the user has no email
   * bypass - this is used to determine if the system_status and local storage check should be bypassed.
   * subProvider - the only valid subProvider is xtract and is used to do sysAdmin login
   * authProvider - determines what 3rd party auth provider to send the user to
   **/

  useEffect(() => {
    // componentDidMount
    // if any modals happen to be open, close them.
    dispatch(closeAllModals())
    setErrorRef.current = setError

    // start a timer to do system status check every (globalConfig.systemCheckFrequency) minutes.
    const statusCheckTimer = setInterval(() => setStatusCheckComplete(false), globalConfig.systemCheckFrequency * 60 * 1000)
    return () => {
      clearInterval(statusCheckTimer)
      setErrorRef.current = () => null
    }
  }, [])

  useEffect(() => {
    // this covers clearing the context query string as well since context always comes with bypass
    if (bypass) {
      // if there was a bypass param we should also save the requested launch context if there was one
      if(queryParams.context) {
        setLaunchContext(queryParams.context)
      }
      // clear the route params
      navigate(pathname)
    }
  }, [bypass])

  const AuthContent = useMemo(() => {
    if (error !== null) {
      return (<AuthError error = {error} />)
    } else if (pathname.endsWith('/callback')) {
      return (<div>attempting token fetch</div>)
    } else if (!storageCheckComplete) {
      return (<StorageCheck setStorageCheckComplete={setStorageCheckComplete} setError={setError}/>)
    } else if (!statusCheckComplete) {
      return (<StatusCheck setStatusCheckComplete={setStatusCheckComplete} subProvider={subProvider}/>)
    } else if (emailAddressRequired) {
      setError('There is no email associated with this account. Please contact your system administrator and have them set the email address on your account.')
    } else if (emailVerificationRequired) {
      setError('The email address associated with this account has not been verified. Please click the verification link in your email to verify your email address before loging in. Contact the system administrator if a new verification email is needed.')
    } else {
      return (<AuthProviderWrapper authProvider={authProvider} subProvider={subProvider} setErrorRef={setErrorRef} loggedOut={auth.loggedOut && auth.auth_provider !== 'summit'}/>)
    }
  }, [
    error,
    statusCheckComplete,
    storageCheckComplete,
    emailVerificationRequired,
    emailAddressRequired,
    authProvider,
    subProvider,
    bypass
  ])

  return (
    <AuthView>
      {AuthContent}
    </AuthView>
  )
}

export default withQueryParams()(AuthPanel)
